import { isEmpty, isEqual } from 'lodash'
import smoothscroll from 'smoothscroll-polyfill'

export const getChangedValues = (values, initialValues) => {
  if (Array.isArray(values)) {
    return values?.reduce((acc, value) => {
      const hasChanged = initialValues?.every(
        (initialValue) => !isEqual(initialValue, value)
      )

      if (hasChanged) {
        return [...acc, value]
      }

      return acc
    }, [])
  }

  return Object.entries(values)?.reduce((acc, [key, value]) => {
    const hasChanged = !isEqual(initialValues[key], value)

    if (hasChanged) {
      acc[key] = value
    }

    return acc
  }, {})
}

export const createRequiredLabel = (label = '') => {
  return (
    <div>
      {label} <span className="color-danger">*</span>
    </div>
  )
}

export const isChangedValue = (initialValue, value) => {
  return initialValue !== value
}

/**
 *
 * findFirstErrorField - Finds the first form field element with a specific error attribute.
 *
 * @param {Object} errors - Object containing errors from Formik.
 * @param {Array} errorFields - Array of fields containing data-error attribute.
 *
 * @returns {HTMLElement|null} The first form field element with the specified error, or null if not found.
 */
export const findFirstErrorField = (errors, errorsFields) => {
  if (isEmpty(errors)) return null

  const firstError = Object.keys(errors)[0]

  return (
    errorsFields?.find(
      (node) => node.getAttribute('data-error') === firstError
    ) || null
  )
}

/**
 *
 * scrollToElement - Smoothly scrolls to a specified element.
 *
 * @param {HTMLElement} element - The element to scroll to.
 */
export const scrollToElement = (element) => {
  if (!element) return null

  requestAnimationFrame(() => {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'center',
    })

    smoothscroll.polyfill()
  })
}

/**
 * scrollToFirstError - scrolls to the first error field
 *
 * @param {Object} errors - Object containing errors from Formik
 * The keys of this object should correspond to the names of the form field "data-error" attribute.
 *
 * Example: { name: 'Required', email: 'Invalid email' }.
 * For nested errors, such as { standsSettings: { tip_values: [] } },
 * provide the nested object like so "errors.standsSettings.[index]"
 *
 * @param {Array} errorsFields - Array of fields containing data-error attribute
 *
 */
export const scrollToFirstError = (errors, errorsFields) => {
  const firstErrorField = findFirstErrorField(errors, errorsFields)

  scrollToElement(firstErrorField)
}
